import { Link } from "react-router-dom";
import { MapPin, Phone, ArrowRight, Building2, Home as HomeIcon } from "lucide-react";
import SEO from "../components/SEO";
import { COMPANY } from "../lib/constants";

const counties = [
  {
    name: "Fairfax County",
    desc: "Comprehensive home care across Fairfax County — including Burke, Annandale, Vienna, Reston, Herndon, and Springfield.",
    keyword: "home care Fairfax County VA",
  },
  {
    name: "Prince William County",
    desc: "Licensed home care for Prince William County families — Woodbridge, Dale City, Gainesville, Haymarket, and Bristow.",
    keyword: "home care Prince William County VA",
  },
  {
    name: "Loudoun County",
    desc: "Skilled nursing and personal care across Loudoun County — Leesburg, Ashburn, Sterling, Purcellville, and Dulles.",
    keyword: "home care Loudoun County VA",
  },
  {
    name: "Arlington County",
    desc: "Compassionate home care across Arlington County — Ballston, Clarendon, Rosslyn, Pentagon City, and Crystal City.",
    keyword: "home care Arlington County VA",
  },
];

const cities = [
  {
    name: "City of Alexandria",
    desc: "Home care for Old Town, Del Ray, Eisenhower, and West End Alexandria neighborhoods.",
    keyword: "home care Alexandria VA",
  },
  {
    name: "City of Fairfax",
    desc: "Personalized home health care for the City of Fairfax, near George Mason and Old Town Fairfax.",
    keyword: "home care City of Fairfax VA",
  },
  {
    name: "City of Manassas",
    desc: "Trusted home care across the City of Manassas, including Old Town and Wellington neighborhoods.",
    keyword: "home care Manassas VA",
  },
  {
    name: "City of Manassas Park",
    desc: "Compassionate, licensed home care for families across the City of Manassas Park.",
    keyword: "home care Manassas Park VA",
  },
  {
    name: "City of Falls Church",
    desc: "Home care for the City of Falls Church, Bailey's Crossroads, Seven Corners, and nearby Tysons-area neighborhoods.",
    keyword: "home care Falls Church VA",
  },
];

function AreaCard({ item, testidPrefix }) {
  const slug = item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return (
    <article
      data-testid={`${testidPrefix}-${slug}`}
      className="group bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-lg transition-all hover:-translate-y-1"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg bg-brand-amber/10 text-brand-amber-dark flex items-center justify-center">
          <MapPin className="w-5 h-5" />
        </div>
        <h3 className="font-heading text-lg font-semibold text-brand-blue-dark">
          {item.name}
        </h3>
      </div>
      <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
      <div className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-brand-blue/80 bg-brand-blue/5 px-3 py-1 rounded-full">
        {item.keyword}
      </div>
    </article>
  );
}

export default function ServiceArea() {
  return (
    <>
      <SEO
        title="Service Area | Home Care in Fairfax, Prince William, Loudoun, Arlington & Beyond"
        description="1st Home Care provides licensed home care services across Northern Virginia — Fairfax, Prince William, Loudoun & Arlington Counties, plus the Cities of Alexandria, Fairfax, Manassas, Manassas Park & Falls Church. Medicaid approved."
        keywords="home care Fairfax County VA, home care Prince William County, home care Loudoun County, home care Arlington VA, home care Alexandria VA, home care Manassas VA, Medicaid home care Northern Virginia"
        canonical="https://1sthomecare.com/service-area"
      />

      <section className="container-pad section-pad">
        <div className="max-w-3xl">
          <div className="text-xs font-bold uppercase tracking-widest text-brand-amber mb-3">
            Where We Serve
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-brand-blue-dark text-balance">
            Home care across Northern Virginia
          </h1>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed">
            1st Home Care Services proudly delivers compassionate, licensed home
            care across <strong>four counties</strong> and <strong>five cities</strong>
            in Northern Virginia. From skilled nursing to respite care, our
            team provides Medicaid-approved, family-centered care wherever you call home.
          </p>
        </div>
      </section>

      {/* COUNTIES */}
      <section data-testid="counties-section" className="container-pad pb-12 md:pb-16">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-brand-blue text-white flex items-center justify-center">
            <Building2 className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-brand-amber">
              Counties Served
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-brand-blue-dark tracking-tight">
              4 Northern Virginia Counties
            </h2>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {counties.map((c) => (
            <AreaCard key={c.name} item={c} testidPrefix="county-card" />
          ))}
        </div>
      </section>

      {/* CITIES */}
      <section data-testid="cities-section" className="container-pad pb-16 md:pb-24">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-brand-amber text-white flex items-center justify-center">
            <HomeIcon className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-brand-amber">
              Cities Served
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-brand-blue-dark tracking-tight">
              5 Independent Cities
            </h2>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {cities.map((c) => (
            <AreaCard key={c.name} item={c} testidPrefix="city-card" />
          ))}
        </div>
      </section>

      {/* SEO copy block */}
      <section data-testid="seo-copy" className="bg-brand-gray/40 section-pad">
        <div className="container-pad max-w-4xl">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-brand-blue-dark tracking-tight">
            Trusted home health care across Northern Virginia
          </h2>
          <div className="mt-5 space-y-4 text-gray-700 leading-relaxed">
            <p>
              Looking for <strong>home care in Fairfax County</strong>,
              <strong> Prince William County</strong>, <strong>Loudoun County</strong>,
              or <strong>Arlington County</strong>? 1st Home Care Services brings
              hospital-grade care into the comfort of your home, with licensed
              nurses and trained caregivers across the entire Northern Virginia region.
            </p>
            <p>
              We also serve the Cities of <strong>Alexandria</strong>,
              <strong> Fairfax</strong>, <strong>Manassas</strong>,
              <strong> Manassas Park</strong>, and <strong>Falls Church</strong> —
              specializing in skilled nursing at home, private duty nursing,
              Medicaid home care, and respite care for seniors, children, and adults
              living with complex medical needs.
            </p>
            <p>
              Care can typically begin within 24–48 hours. Our administrator and
              Director of Nursing personally reviews every care plan to ensure it
              meets the highest standards of safety, compassion, and clinical excellence.
            </p>
          </div>
        </div>
      </section>

      <section className="container-pad section-pad text-center">
        <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-brand-blue-dark tracking-tight">
          Don't see your city or county? We may still be able to help.
        </h2>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <a
            href={`tel:${COMPANY.phoneTel}`}
            data-testid="area-call-now"
            className="inline-flex items-center gap-2 bg-brand-blue text-white font-semibold px-7 py-4 rounded-full shadow-md hover:bg-brand-blue-dark"
          >
            <Phone className="w-4 h-4" /> {COMPANY.phone}
          </a>
          <Link
            to="/contact"
            data-testid="area-contact"
            className="inline-flex items-center gap-2 bg-brand-amber text-white font-semibold px-7 py-4 rounded-full shadow-md hover:bg-brand-amber-dark"
          >
            Contact Us <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </>
  );
}
